/**
 * The destination a drawer is working on, as a badge for its `WizardHeader`: the brand mark, then
 * the destination's display name.
 *
 * Shared by the Add and the Edit drawer, which both carried their own copy with a `KeyRound` glyph
 * in place of the mark. That read as a bug rather than a choice: the add flow's own type picker one
 * screen earlier renders the real brand mark for every row (`AccountTypeRow`), so choosing "Facebook
 * Custom Audience" by its Meta mark and landing on a header showing a key looked like the wrong
 * account had been selected. A key also says nothing a badge reading "Facebook Custom Audience"
 * does not already say, where the mark is the thing that identifies a destination at a glance.
 *
 * `KeyRound` is still right elsewhere in this folder — beside an AUTH type ("API key"), which is
 * what a key actually denotes.
 */
import { Badge } from '@/_shared/components/ui/badge';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';

interface DestinationBadgeProps {
  /** The platform's destination type — resolved through the registry, never by importing a mark. */
  destinationType: string;
  label: string;
}

export function DestinationBadge({ destinationType, label }: DestinationBadgeProps): React.JSX.Element {
  // Decorative by construction: every mark the registry returns is already `aria-hidden` (a brand
  // `<img>` with an empty alt, or the generic `Send`). The badge's own text is the accessible name.
  const Mark = destinationTypeIcon(destinationType).Icon;
  return (
    <Badge variant="outline" className="text-xs text-muted-foreground">
      {/* `size-5`, where the KeyRound glyph it replaced used `size-3.5`. A Lucide stroke reads fine
          at 14px; a brand asset does not, and a WORDMARK least of all — Braze letterboxes into a
          square box at 46% of its height, so the box has to be generous for it to be legible at all.
          `size-5` is also what the type-picker rows one screen earlier give the very same marks, so
          the chosen destination does not shrink on the way to this header. */}
      <Mark className="size-5" />
      {label}
    </Badge>
  );
}
